import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { motion } from 'framer-motion';
import { FaDollarSign, FaSave, FaHospital } from 'react-icons/fa';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';
import { apiGet, apiPost } from '../../../shared/utils/apiConfig';
import { ToastContainer } from 'react-toastify';

const DoctorConsultationPrice = () => {
    const { setTitle } = useTitle();
    const [clinics, setClinics] = useState([]);
    const [selectedClinicId, setSelectedClinicId] = useState('');
    const [consultationPrice, setConsultationPrice] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingClinics, setLoadingClinics] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setTitle('سعر الكشف');
        fetchClinics();
    }, [setTitle]);

    useEffect(() => {
        if (selectedClinicId) {
            fetchConsultationPrice();
        } else {
            setConsultationPrice('');
        }
    }, [selectedClinicId]);

    const fetchClinics = async () => {
        try {
            setLoadingClinics(true);
            const data = await apiGet('/doctor/my_clinics');
            const list = data?.result || data?.data || [];
            setClinics(Array.isArray(list) ? list : []);
            if (Array.isArray(list) && list.length > 0) {
                setSelectedClinicId(String(list[0].id ?? list[0].clinic_id ?? ''));
            }
        } catch (err) {
            showErrorToast('فشل تحميل العيادات');
        } finally {
            setLoadingClinics(false);
        }
    };

    const extractPriceFromResponse = (response) => {
        if (!response) return '';

        const candidates = [
            response.price,
            response.result?.price,
            response.result?.consultation_price,
            response.data?.price,
            response.data?.consultation_price,
            response.data?.result?.price,
            response.data?.result?.consultation_price,
            response.result?.data?.price,
            response.result?.data?.consultation_price
        ];

        for (const value of candidates) {
            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }

        return '';
    };

    const fetchConsultationPrice = async () => {
        if (!selectedClinicId) return;

        setLoading(true);
        try {
            // استخدم الاندبوينت الجديد هنا
            const response = await apiGet('/doctor_dash/show_doctor_clinic_price', {
                clinic_id: selectedClinicId
            });

            const isSuccess =
                response?.status === 'success' ||
                response?.statusCode === 200 ||
                response?.data?.status === 'success';

            if (isSuccess) {
                const price = extractPriceFromResponse(response);
                setConsultationPrice(price !== '' ? String(price) : '');
            } else {
                setConsultationPrice('');
            }
        } catch (error) {
            setConsultationPrice('');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedClinicId) {
            showErrorToast('يرجى اختيار العيادة');
            return;
        }
        
        if (!consultationPrice || consultationPrice.trim() === '') {
            showErrorToast('يرجى إدخال سعر الكشف');
            return;
        }

        const priceValue = parseFloat(consultationPrice);
        if (isNaN(priceValue) || priceValue < 0) {
            showErrorToast('يرجى إدخال سعر صحيح');
            return;
        }

        try {
            setSaving(true);
            const response = await apiPost('/doctor_dash/change_doctor_clinic_price', {
                clinic_id: Number(selectedClinicId),
                price: priceValue
            });

            const isSuccess =
                response?.status === 'success' ||
                response?.statusCode === 200 ||
                response?.data?.status === 'success';

            if (isSuccess) {
                showSuccessToast(response?.message || response?.data?.message || 'تم حفظ سعر الكشف بنجاح');
                await fetchConsultationPrice();
            } else {
                throw new Error(response?.message || response?.data?.message || 'فشل في حفظ سعر الكشف');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في حفظ سعر الكشف');
        } finally {
            setSaving(false);
        }
    };

    if (loadingClinics) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل العيادات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                <ToastContainer />
                
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">سعر الكشف</h4>
                </div>

                {/* Price Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-lg border-0">
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-12 mb-4">
                                    <label className="form-label fw-bold mb-2">
                                        <FaHospital className="mx-2" />
                                        اختر العيادة
                                    </label>
                                    <select
                                        className="form-select form-select-lg"
                                        value={selectedClinicId}
                                        onChange={(e) => setSelectedClinicId(e.target.value)}
                                        disabled={loadingClinics}
                                        required
                                        style={{
                                            fontSize: '1.1rem',
                                            padding: '0.75rem 1rem'
                                        }}
                                    >
                                        <option value="">
                                            {loadingClinics ? 'جاري التحميل...' : 'اختر العيادة'}
                                        </option>
                                        {clinics.map((c) => (
                                            <option key={c.id ?? c.clinic_id} value={String(c.id ?? c.clinic_id)}>
                                                {c.title || c.name || c.clinic_name || `عيادة رقم ${c.id ?? c.clinic_id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 mb-4">
                                    <label className="form-label fw-bold mb-2">
                                        <FaDollarSign className="mx-2" />
                                        سعر الكشف (بالجنيه المصري)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control form-control-lg"
                                        placeholder="أدخل سعر الكشف"
                                        value={consultationPrice}
                                        onChange={(e) => setConsultationPrice(e.target.value)}
                                        min="0"
                                        step="0.01"
                                        required
                                        disabled={!selectedClinicId || loading}
                                        style={{
                                            fontSize: '1.1rem',
                                            padding: '0.75rem 1rem'
                                        }}
                                    />
                                    <small className="form-text text-muted mt-2">
                                        {!selectedClinicId ? 'يرجى اختيار العيادة أولاً' : 'يرجى إدخال سعر الكشف بالجنيه المصري'}
                                    </small>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-3 mt-4">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="submit"
                                    className="btn btn-primary d-flex align-items-center gap-2"
                                    disabled={saving}
                                    style={{
                                        backgroundColor: '#184194',
                                        border: 'none',
                                        padding: '0.75rem 2rem',
                                        fontSize: '1rem'
                                    }}>
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            جاري الحفظ...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave />
                                            حفظ
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default DoctorConsultationPrice;

